/**
 * Analytics Service
 * HTTP service for analytics and reporting
 */
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import {
  AnalyticsOverview,
  DocumentAnalyticsRequest,
  DocumentAnalyticsResponse,
  ApiUsageAnalyticsRequest,
  ApiUsageAnalyticsResponse,
  CostAnalyticsRequest,
  CostAnalyticsResponse,
  TemplateAnalyticsResponse,
  ProviderAnalyticsResponse,
  ExportRequest
} from '../models/analytics.models';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/analytics`;
  private readonly skipErrorHeaders = new HttpHeaders().set('X-Skip-Error-Notification', 'true');

  /**
   * Get analytics overview with summary metrics
   */
  getOverview(startDate?: Date, endDate?: Date): Observable<AnalyticsOverview> {
    let params = new HttpParams();

    if (startDate) {
      params = params.set('start_date', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('end_date', endDate.toISOString());
    }

    return this.http.get<AnalyticsOverview>(`${this.apiUrl}/overview`, {
      params,
      headers: this.skipErrorHeaders
    });
  }

  /**
   * Get document processing analytics
   */
  getDocumentAnalytics(request: DocumentAnalyticsRequest): Observable<DocumentAnalyticsResponse> {
    let params = new HttpParams();

    if (request.start_date) {
      params = params.set('start_date', request.start_date.toISOString());
    }
    if (request.end_date) {
      params = params.set('end_date', request.end_date.toISOString());
    }
    if (request.group_by) {
      params = params.set('group_by', request.group_by);
    }

    return this.http.get<DocumentAnalyticsResponse>(`${this.apiUrl}/documents`, {
      params,
      headers: this.skipErrorHeaders
    });
  }

  /**
   * Get API usage analytics
   */
  getApiUsageAnalytics(request: ApiUsageAnalyticsRequest): Observable<ApiUsageAnalyticsResponse> {
    let params = new HttpParams();

    if (request.start_date) {
      params = params.set('start_date', request.start_date.toISOString());
    }
    if (request.end_date) {
      params = params.set('end_date', request.end_date.toISOString());
    }
    if (request.group_by) {
      params = params.set('group_by', request.group_by);
    }

    return this.http.get<ApiUsageAnalyticsResponse>(`${this.apiUrl}/api-usage`, {
      params,
      headers: this.skipErrorHeaders
    });
  }

  /**
   * Get cost analytics
   */
  getCostAnalytics(request: CostAnalyticsRequest): Observable<CostAnalyticsResponse> {
    let params = new HttpParams();

    if (request.start_date) {
      params = params.set('start_date', request.start_date.toISOString());
    }
    if (request.end_date) {
      params = params.set('end_date', request.end_date.toISOString());
    }
    if (request.group_by) {
      params = params.set('group_by', request.group_by);
    }

    return this.http.get<CostAnalyticsResponse>(`${this.apiUrl}/costs`, {
      params,
      headers: this.skipErrorHeaders
    });
  }

  getTemplateAnalytics(startDate?: Date, endDate?: Date): Observable<TemplateAnalyticsResponse> {
    let params = new HttpParams();
    if (startDate) {
      params = params.set('start_date', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('end_date', endDate.toISOString());
    }
    return this.http.get<TemplateAnalyticsResponse>(`${this.apiUrl}/templates`, {
      params,
      headers: this.skipErrorHeaders
    });
  }

  getProviderAnalytics(startDate?: Date, endDate?: Date): Observable<ProviderAnalyticsResponse> {
    let params = new HttpParams();
    if (startDate) {
      params = params.set('start_date', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('end_date', endDate.toISOString());
    }
    return this.http.get<ProviderAnalyticsResponse>(`${this.apiUrl}/providers`, {
      params,
      headers: this.skipErrorHeaders
    });
  }

  /**
   * Export analytics report
   * Downloads the file directly
   */
  exportReport(request: ExportRequest): Observable<Blob> {
    let params = new HttpParams()
      .set('report_type', request.report_type)
      .set('format', request.format);

    if (request.start_date) {
      params = params.set('start_date', request.start_date.toISOString());
    }
    if (request.end_date) {
      params = params.set('end_date', request.end_date.toISOString());
    }

    return this.http.post(`${this.apiUrl}/export`, {}, {
      params,
      responseType: 'blob',
      headers: new HttpHeaders().set('X-Skip-Error-Notification', 'true')
    });
  }

  /**
   * Helper method to download exported file
   */
  downloadExportedFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get default date range (last 30 days)
   */
  getDefaultDateRange(): { start_date: Date; end_date: Date } {
    const end_date = new Date();
    const start_date = new Date();
    start_date.setDate(start_date.getDate() - 30);

    return { start_date, end_date };
  }

  /**
   * Get date range presets
   */
  getDateRangePresets(): Array<{ label: string; value: string; getDates: () => { start_date: Date; end_date: Date } }> {
    return [
      {
        label: 'Last 7 Days',
        value: 'last_7_days',
        getDates: () => {
          const end_date = new Date();
          const start_date = new Date();
          start_date.setDate(start_date.getDate() - 7);
          return { start_date, end_date };
        }
      },
      {
        label: 'Last 30 Days',
        value: 'last_30_days',
        getDates: () => {
          const end_date = new Date();
          const start_date = new Date();
          start_date.setDate(start_date.getDate() - 30);
          return { start_date, end_date };
        }
      },
      {
        label: 'Last 90 Days',
        value: 'last_90_days',
        getDates: () => {
          const end_date = new Date();
          const start_date = new Date();
          start_date.setDate(start_date.getDate() - 90);
          return { start_date, end_date };
        }
      },
      {
        label: 'This Month',
        value: 'this_month',
        getDates: () => {
          const now = new Date();
          const start_date = new Date(now.getFullYear(), now.getMonth(), 1);
          const end_date = new Date();
          return { start_date, end_date };
        }
      },
      {
        label: 'Last Month',
        value: 'last_month',
        getDates: () => {
          const now = new Date();
          const start_date = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const end_date = new Date(now.getFullYear(), now.getMonth(), 0);
          return { start_date, end_date };
        }
      },
      {
        label: 'This Year',
        value: 'this_year',
        getDates: () => {
          const now = new Date();
          const start_date = new Date(now.getFullYear(), 0, 1);
          const end_date = new Date();
          return { start_date, end_date };
        }
      }
    ];
  }
}

